import { useEffect, useRef } from 'react';
import moment from 'moment-timezone';
// Component: useLiveUpdates.js
// Purpose: Merge live ticks into SmartChart bars and mounted chart series.

import { extractFieldValue, barVolume } from './utils';
import { toUnixSec } from '../../utils/tradeMarkerBuilder';
import { liveNodeChartValue } from '../../pages/Strategies/Modern/StrategyBuilder/config/nodeVisualConfig';


//--> Binary-search a time-sorted bar array for a bucket. Returns index or -1.
const findBarIndexByTime = (bars, targetTime) => {
    let lo = 0;
    let hi = bars.length - 1;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const t = bars[mid].time;
        if (t === targetTime) return mid;
        if (t < targetTime) lo = mid + 1;
        else hi = mid - 1;
    }
    return -1;
};

//--> Merge a tick into a bar. Used by equal-time AND backward-upsert branches.
const mergeTickIntoBar = (bar, tickPrice, volumeToUse, chartType) => {
    if (chartType === 'heikin') {
        return {
            ...bar,
            high: Math.max(bar.high, tickPrice),
            low: Math.min(bar.low, tickPrice),
            close: tickPrice,
            value: (bar.value || 0) + volumeToUse,
            color: bar.color, // HA colour is fixed at bar-open time; do not flip on tick.
        };
    }
    return {
        ...bar,
        high: Math.max(bar.high, tickPrice),
        low: Math.min(bar.low, tickPrice),
        close: tickPrice,
        value: tickPrice,
        volume: (bar.volume || 0) + volumeToUse,
    };
};

//--> rAF coalescer. Keyed by bar.time → latest bar payload. Flush applies every
//*   queued bar (not just the first) to every mounted series exactly once per
//*   animation frame. This avoids burst-repaint during simulator rewind.
//*
//*   `mode` and `graphNodeIds` are carried through the call chain so the
//*   graph-node gate (Chunk 6) is applied at flush time, not at enqueue time.
//*   They are captured per-call in a `ctx` object so a single module-level
//*   coalescer serves all currently-rendering charts (YAGNI: multi-chart is
//*   not an active scenario today).
let pendingRaf = null;
const pendingBars = new Map();    // bar.time → bar
let pendingCtx = null;            // last-call ctx wins (seriesRefs, mode, graphNodeIds, ...)

const flushPendingBars = () => {
    pendingRaf = null;
    const ctx = pendingCtx;
    pendingCtx = null;
    if (!ctx) { pendingBars.clear(); return; }
    // Snapshot + clear so bars re-enqueued during paint schedule a fresh frame.
    const toFlush = Array.from(pendingBars.values());
    pendingBars.clear();
    toFlush.forEach((bar) => applyBarToSeries(ctx, bar));
};

//--> Apply a single bar to every mounted series respecting the graph-node gate.
//*   `ctx = { seriesRefs, volumeSeriesRef, layers, volColor, mode, graphNodeIds, liveNodeResults }`
const applyBarToSeries = (ctx, bar) => {
    const { seriesRefs, volumeSeriesRef, layers, volColor, mode, graphNodeIds, liveNodeResults, indicatorIdToSeriesMap, suppressLiveNodeResults } = ctx;
    //--> When deployment_tf != chart_tf, treat liveNodeResults as null (gate at the
    //*  apply layer so the existing graph-node fallback path doesn't re-overwrite).
    const effectiveLiveNodeResults = suppressLiveNodeResults ? null : liveNodeResults;
    if (seriesRefs.current && seriesRefs.current.length > 0) {
        seriesRefs.current.forEach((seriesObj) => {
            if (!seriesObj.instance) return;
            //--> Graph-node gate: in live/paper/rd, useIndicators.indicatorMaps is the
            //*   single authoritative source for graph-node indicators (populated via
            //*   live-merge + historic-merge effects). Suppress the local tick-by-tick
            //*   overwrite unless liveNodeResults provides a deliberate override.
            if ((mode === 'live' || mode === 'paper' || mode === 'rd') && graphNodeIds?.has(seriesObj.id)) {
                if (effectiveLiveNodeResults && effectiveLiveNodeResults[seriesObj.id] !== undefined) {
                    //* 142-42: full output dict (metadata.outputs) when the node was reduced
                    const innerData = liveNodeChartValue(effectiveLiveNodeResults[seriesObj.id]);
                    
                    let numVal = innerData;
                    const sKey = seriesObj.instance?.seriesKey;
                    if (innerData && typeof innerData === 'object') {
                        if (sKey && innerData[sKey] !== undefined) {
                            numVal = innerData[sKey];
                        }
                    }

                    if (numVal !== undefined && numVal !== null && !Number.isNaN(Number(numVal))) {
                        seriesObj.instance.update({ time: bar.time, value: Number(numVal) });
                    }
                }
                return;
            }
            const sType = seriesObj.type;
            const sId = seriesObj.id;
            const layerConfig = (layers || []).find((l) => l.id === sId);
            const field = layerConfig?.styleOptions?.field || 'CLOSE';
            const value = extractFieldValue(bar, field, true);
            if (sType === 'line' || sType === 'area') {
                if (value !== undefined && value !== null && !Number.isNaN(value)) {
                    seriesObj.instance.update({ time: bar.time, value });
                }
            } else if (sType === 'heikin' || sType === 'candle') {
                seriesObj.instance.update({
                    time: bar.time,
                    open: bar.open,
                    high: bar.high,
                    low: bar.low,
                    close: bar.close,
                    color: bar.color,
                });
            }
        });
    }
    
    //--> Update decoupled/overlay indicators directly based on live results
    if (indicatorIdToSeriesMap?.current) {
        indicatorIdToSeriesMap.current.forEach((seriesInstanceArray, id) => {
            if (!seriesInstanceArray || !Array.isArray(seriesInstanceArray)) return;
            if (effectiveLiveNodeResults && effectiveLiveNodeResults[id] !== undefined) {
                const val = effectiveLiveNodeResults[id];
                
                //* 142-42: full output dict (metadata.outputs) when the node was reduced — a p_* line
                //*   reads its own key, never the configured scalar (`call`)
                const innerData = liveNodeChartValue(val);
                seriesInstanceArray.forEach(seriesInstance => {
                    
                    let numVal = innerData;
                    const sKey = seriesInstance.seriesKey;
                    if (innerData && typeof innerData === 'object') {
                        if (sKey && innerData[sKey] !== undefined) {
                            numVal = innerData[sKey];
                        }
                    }

                    if (numVal !== undefined && numVal !== null && !Number.isNaN(Number(numVal))) {
                        seriesInstance.update({ time: bar.time, value: Number(numVal) });
                    }
                });
            }
        });
    }

    if (volumeSeriesRef.current) {
        const volValue = barVolume(bar);
        volumeSeriesRef.current.update({ time: bar.time, value: volValue, color: volColor });
    }
};

//--> Public entry: forward path applies immediately; backward path coalesces.
//*   `opts = { coalesce, mode, graphNodeIds }` — mode/graphNodeIds are the
//*   gate inputs from the hook's current call (see Chunk 6 Task 6.2).
const updateChartSeriesForBar = (
    seriesRefs, volumeSeriesRef, bar, layers, volColor, opts = {}
) => {
    const { coalesce = false, mode, graphNodeIds, liveNodeResults, indicatorIdToSeriesMap, suppressLiveNodeResults = false } = opts;
    const ctx = { seriesRefs, volumeSeriesRef, layers, volColor, mode, graphNodeIds, liveNodeResults, indicatorIdToSeriesMap, suppressLiveNodeResults };
    if (coalesce) {
        pendingBars.set(bar.time, bar);
        pendingCtx = ctx;
        if (pendingRaf == null) {
            pendingRaf = requestAnimationFrame(flushPendingBars);
        }
    } else {
        applyBarToSeries(ctx, bar);
    }
};


/**
 * useLiveUpdates Hook
 * Handles real-time tick ingestion and aggregation for SmartChart.
 *
 * @param {Object} params
 * @param {string} params.mode - 'live' | 'backtest'
 * @param {Object} params.liveTick - Latest tick from WebSocket
 * @param {string} params.timeframe - Current timeframe ('1m', '5m', '1h', etc.)
 * @param {Object} params.dataRef - Ref to current chart data (for aggregation) (modified in place)
 * @param {Function} params.setData - State setter to trigger re-renders if needed (though we mostly mutate ref + series)
 * @param {Object} params.seriesRefs - Ref to chart series instances
 * @param {Object} params.volumeSeriesRef - Ref to volume series instance
 * @param {string} params.chartType - 'candle', 'line', 'heikin', etc.
 * @param {Array} params.layers - Active layers configuration
 * @param {Function} params.onBarUpdate - Optional callback fired with the current partial bar on each tick
 * @param {Set} params.graphNodeIds - NEW (Chunk 6 callers supply this Set; pre-Chunk-6 it is undefined)
 */
export const useLiveUpdates = ({
    mode,
    liveTick,
    liveNodeResults,
    liveBarTimeframe,    //--> NEW: WS message's primary TF; gates direct-series-updates against chart_tf.
    timeframe,
    dataRef,
    setData,
    seriesRefs,
    indicatorIdToSeriesMap,
    volumeSeriesRef,
    chartType,
    layers,
    onBarUpdate,
    graphNodeIds,    //--> NEW (Chunk 6 callers supply this Set; pre-Chunk-6 it is undefined)
}) => {
    //--> Compute once per render whether we should apply liveNodeResults at all.
    //*  When deployment_tf != chart_tf, the held liveNodeResults snapshot was
    //*  computed on a different OHLC granularity than the chart's bars, and
    //*  stamping it at chart-tf bar.time positions creates V-shape artifacts.
    //*  In that case, suppress the direct-series-update paths and let the
    //*  cache-derived series stand.
    const suppressLiveNodeResults = !!(liveBarTimeframe && timeframe && liveBarTimeframe !== timeframe);
    // Keep track of the 'current' bar's start time to detect changes cheaply
    const lastTickTimeRef = useRef(null);

    /**
     * Helper: Align timestamp to timeframe bucket
     */
    const alignToTimeframe = (timestamp, tf) => {
        const m = moment.unix(timestamp);
        switch (tf) {
            case '1m': return m.startOf('minute').unix();
            case '3m': return timestamp - (timestamp % (3 * 60));
            case '5m': return timestamp - (timestamp % (5 * 60));
            case '15m': return timestamp - (timestamp % (15 * 60));
            case '30m': return timestamp - (timestamp % (30 * 60));
            case '1h': return m.startOf('hour').unix();
            case '2h': return m.startOf('hour').unix() - (m.startOf('hour').unix() % (2 * 3600));
            case '4h': return m.startOf('hour').unix() - (m.startOf('hour').unix() % (4 * 3600));
            case '1D': return m.startOf('day').unix(); // Uses local/system timezone of moment unless timezone set globally
            case '1W': return m.startOf('isoWeek').unix();
            case '1M': return m.startOf('month').unix();
            default: return m.startOf('minute').unix(); // Fallback to 1m
        }
    };

    /**
     * Helper: Hex to RGBA (for volume colors)
     */
    const hexToRgba = (hex, opacity = 100) => {
        if (!hex) return hex;
        if (hex.startsWith('rgba')) return hex;
        let c;
        if(/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)){
            c= hex.substring(1).split('');
            if(c.length== 3){
                c= [c[0], c[0], c[1], c[1], c[2], c[2]];
            }
            c= '0x'+c.join('');
            return 'rgba('+[(c>>16)&255, (c>>8)&255, c&255].join(',')+','+(opacity/100)+')';
        }
        return hex;
    };

    useEffect(() => {
        if (mode === 'backtest') return;
        if (!liveTick) return;

        // Ensure we have a valid price
        const tickPrice = Number(liveTick.ltp || liveTick.last_price);
        if (tickPrice === undefined || tickPrice === null || isNaN(tickPrice)) return;

        // 1. Calculate Time Bucket
        // Prefer the tick's own timestamp so simulated ticks (exchange_time = historical)
        // bucket into the right bar alongside existing data. Fall back to wall-clock only
        // when the tick lacks a timestamp field (shouldn't happen in practice).
        let tickSeconds;
        if (liveTick?.exchange_time) {
            tickSeconds = toUnixSec(liveTick.exchange_time);
            //! toUnixSec may return null for invalid timestamps; fall back to wall-clock
            //! so the tick bucket always resolves (same safety the pre-migration moment
            //! call had — invalid moments returned NaN which the downstream code tolerated
            //! less gracefully). Preserves original exchange_time preference.
            if (tickSeconds == null) tickSeconds = moment().unix();
        } else if (typeof liveTick?.time === 'number') {
            tickSeconds = liveTick.time > 1e12 ? Math.floor(liveTick.time / 1000) : liveTick.time;
        } else {
            tickSeconds = moment().unix();
        }
        const tickTime = alignToTimeframe(tickSeconds, timeframe);

        const currentData = dataRef.current;
        const lastCandle = currentData.length > 0 ? currentData[currentData.length - 1] : null;

        // 2. Prepare visual props (needed for both backward and forward paths)
        const tickVolume = Number(liveTick.volume || liveTick.v || 0);  // Note: This might be cumulative or instantaneous depending on broker
        // Most broker feeds send 'volume traded so far today' or 'volume of this tick'.
        // Assuming 'tick volume' for aggregation unless we track cumulative diff.
        // If broker sends 'LTT' (last trade time) volume, we just add it.
        // Checks needed: Does liveTick.volume mean 'total volume today'?
        // If yes, we need to diff. If no (it's trade volume), we sum.
        // *Assumption*: Standard aggregators send 'volume' as the size of the trade(s) causing the update.

        const volumeToUse = tickVolume; // Simplified for now.

        let isUp = true; // Default
        if (lastCandle) {
             const refPrice = lastCandle.close || lastCandle.value;
             isUp = tickPrice >= refPrice;
        }

        // Extract Volume Styles
        const volumeLayer = (layers || []).find(l => l.type === 'volume');
        const volStyles = volumeLayer?.styleOptions || {};
        const volUpColor = volStyles.upColor || 'rgba(0, 194, 146, 0.5)';
        const volDownColor = volStyles.downColor || 'rgba(228, 106, 118, 0.5)';
        const opacity = volStyles.volumeOpacity !== undefined ? volStyles.volumeOpacity : 50;
        const volColor = hexToRgba(isUp ? volUpColor : volDownColor, opacity);

        // 3. Backward tick guard: upsert-in-place or drop (integrity).
        // Simulator rewinds, WS reconnect backfills, and mildly out-of-order broker
        // deliveries all land here instead of being silently dropped.
        if (lastCandle && tickTime < lastCandle.time) {
            const idx = findBarIndexByTime(currentData, tickTime);
            if (idx < 0) {
                // Older than anything we have; genuine integrity risk. Drop + one-time warn.
                if (!useLiveUpdates._warnedOldTick) {
                    console.warn('[useLiveUpdates] Dropping tick older than first bar', { tickTime, firstBarTime: currentData[0]?.time });
                    useLiveUpdates._warnedOldTick = true;
                }
                return;
            }
            const merged = mergeTickIntoBar(currentData[idx], tickPrice, volumeToUse, chartType);
            dataRef.current[idx] = merged;
            updateChartSeriesForBar(
                seriesRefs, volumeSeriesRef, merged, layers, volColor,
                { coalesce: true, mode, graphNodeIds, liveNodeResults, indicatorIdToSeriesMap, suppressLiveNodeResults },
            );
            if (onBarUpdate) onBarUpdate(merged);
            return;
        }

        // 4. Update Logic
        try {
            let updatedCandle;

            // A. Update Existing Candle
            if (lastCandle && tickTime === lastCandle.time) {
                updatedCandle = mergeTickIntoBar(lastCandle, tickPrice, volumeToUse, chartType);
                // HA colour must be recalculated here (open is fixed at bar start, not prev HA)
                if (chartType === 'heikin') {
                    updatedCandle = {
                        ...updatedCandle,
                        color: tickPrice >= lastCandle.open
                            ? (volStyles.upColor || 'rgba(0, 194, 146, 0.5)')
                            : (volStyles.downColor || 'rgba(228, 106, 118, 0.5)'),
                    };
                }
                dataRef.current[currentData.length - 1] = updatedCandle;
            }
            // B. Create New Candle
            else {
                if (chartType === 'heikin') {
                    const prevHA = lastCandle || { open: tickPrice, close: tickPrice };
                    const haOpen = (prevHA.open + prevHA.close) / 2;
                    const haClose = tickPrice;

                    updatedCandle = {
                        time: tickTime,
                        open: haOpen,
                        high: Math.max(haOpen, haClose),
                        low: Math.min(haOpen, haClose),
                        close: haClose,
                        value: volumeToUse,
                        color: haClose >= haOpen ? volUpColor : volDownColor
                    };
                } else {
                    updatedCandle = {
                        time: tickTime,
                        open: tickPrice,
                        high: tickPrice,
                        low: tickPrice,
                        close: tickPrice,
                        value: tickPrice,
                        volume: volumeToUse
                    };
                }

                //! Duplicate-time guard. Race: useChartData may concurrently schedule
                //! setData(standardData) that already includes this bar (pagination /
                //! window-crossing refetch). Without this guard, a functional prev
                //! derived from the post-replacement state would append a second copy
                //! at the same time, breaking lw-charts' strict-ascending invariant
                //! (setData throws "data must be asc ordered by time"), which silently
                //! kills overlay series like ChandelierOverlay from rendering forward.
                dataRef.current.push(updatedCandle);
                setData(prev => {
                    if (prev.length > 0) {
                        const last = prev[prev.length - 1];
                        if (last.time === updatedCandle.time) {
                            //--> prev already has this bar (from a racing historic replace);
                            //    upsert in place instead of appending a duplicate.
                            const next = prev.slice(0, -1);
                            next.push(updatedCandle);
                            return next;
                        }
                        if (last.time > updatedCandle.time) {
                            //--> prev is ahead of us (shouldn't happen — backward guard
                            //    at line 309 handles it); bail out without mutation.
                            return prev;
                        }
                    }
                    return [...prev, updatedCandle];
                });
            }

            //--> Notify parent of the current (possibly partial) bar so Core Data
            //    in the R&D Watch dashboard updates as ticks flow. Safe no-op when
            //    parent doesn't pass a callback.
            if (onBarUpdate && updatedCandle) {
                onBarUpdate(updatedCandle);
            }

            // 5. Update Chart Series (Visuals) via shared helper
            updateChartSeriesForBar(
                seriesRefs, volumeSeriesRef, updatedCandle, layers, volColor,
                { coalesce: false, mode, graphNodeIds, liveNodeResults, indicatorIdToSeriesMap, suppressLiveNodeResults },
            );

        } catch (err) {
            console.warn("[useLiveUpdates] Error updating chart:", err);
        }

    }, [liveTick, mode, chartType, timeframe, JSON.stringify(layers), liveNodeResults]); // Added timeframe dependency
};
